import React, { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Text from "components/Text/Text"
import { Guid, IApiLink } from "@/types"
import useAssetVersions from "../hooks/useAssetVersions"
import VersionCollapsible from "../VersionCollapsible"
import AssetPictures from "components/AssetPictures/AssetPictures"
import toastMessages from "stores/toastMessages"

interface IAssetPicturesWithVersionsProps {
    assetId: Guid
    lmid: string
    scrollEl?: React.RefObject<HTMLDivElement | null>
    linkGetAssetVersions?: IApiLink
    currentAssetVersion?: number
    isCurrentVersion?: boolean
}

const AssetPicturesWithVersions: FC<IAssetPicturesWithVersionsProps> = ({
    assetId,
    lmid,
    scrollEl,
    linkGetAssetVersions,
    currentAssetVersion,
    isCurrentVersion = true,
}) => {
    const { t } = useTranslation()
    const { assetVersions } = useAssetVersions(linkGetAssetVersions, toastMessages.showSystemError)

    // Sort versions: current first, then by version number descending
    const sortedVersions = useMemo(() => {
        if (!assetVersions) return null
        return [...assetVersions].sort((a, b) => {
            if (a.isCurrentVersion) return -1
            if (b.isCurrentVersion) return 1
            return b.version - a.version
        })
    }, [assetVersions])

    // Find the version being viewed (might not be current version)
    const viewedVersion = useMemo(() => {
        if (!assetVersions || currentAssetVersion === undefined) return null
        return assetVersions.find(v => v.version === currentAssetVersion)
    }, [assetVersions, currentAssetVersion])

    // If viewing a previous version, only show that version's content (no version dropdowns)
    if (!isCurrentVersion) {
        return (
            <AssetPictures
                assetId={viewedVersion?.assetId ?? assetId}
                lmid={lmid}
                scrollEl={scrollEl}
                readOnly
            />
        )
    }

    // If no versions available or just one version, show the regular AssetPictures
    if (!linkGetAssetVersions || !sortedVersions || sortedVersions.length <= 1) {
        return (
            <AssetPictures
                assetId={assetId}
                lmid={lmid}
                scrollEl={scrollEl}
            />
        )
    }

    // Get previous versions (non-current)
    const previousVersions = sortedVersions.filter(v => !v.isCurrentVersion)

    return (
        <>
            {/* Current version content shown directly without dropdown */}
            <AssetPictures
                assetId={assetId}
                lmid={lmid}
                scrollEl={scrollEl}
            />

            {/* Previous versions shown as collapsible dropdowns */}
            {previousVersions.length > 0 && (
                <div className="mt-4">
                    <Text type="h4" className="mb-2 color-grey-600">{t("asset.previousVersions")}</Text>
                    {previousVersions.map((version) => (
                        <VersionCollapsible
                            key={version.assetId}
                            version={version}
                            defaultExpanded={false}
                        >
                            {(isExpanded) => isExpanded && (
                                <AssetPictures
                                    assetId={version.assetId}
                                    lmid={lmid}
                                    scrollEl={scrollEl}
                                    hideTitle
                                    readOnly
                                />
                            )}
                        </VersionCollapsible>
                    ))}
                </div>
            )}
        </>
    )
}

export default AssetPicturesWithVersions
